import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Chip,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  LinearProgress,
  Switch,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const AppUpdateAdmin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const token = sessionStorage.getItem("token");
  const role = sessionStorage.getItem("role");

  const [currentConfig, setCurrentConfig] = useState(null);
  const [latestVersion, setLatestVersion] = useState("");
  const [releaseNotes, setReleaseNotes] = useState("");
  const [forceUpdate, setForceUpdate] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadedApkUrl, setUploadedApkUrl] = useState("");
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });
  const dashboardPath = "/EmployeeDashboard";

  useEffect(() => {
    if (!token) {
      const redirect = encodeURIComponent(`${location.pathname}${location.search || ""}`);
      navigate(`/login?redirect=${redirect}`, { replace: true });
      return;
    }
    if (role !== "admin") {
      setStatus({ type: "error", message: "Only admin can access this page." });
      setLoading(false);
      return;
    }
    fetchConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BASE_URL}/api/app-update/config?platform=android`);
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Unable to fetch app update config");
      }
      const cfg = data.data;
      setCurrentConfig(cfg);
      setLatestVersion(cfg.latestVersion || "");
      setReleaseNotes(cfg.releaseNotes || "");
      setForceUpdate(Boolean(cfg.forceUpdate));
      setUploadedApkUrl(cfg.apkUrl || "");
    } catch (error) {
      setStatus({ type: "error", message: error.message || "Unable to load config." });
    } finally {
      setLoading(false);
    }
  };

  const onFilePicked = (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".apk")) {
      setStatus({ type: "error", message: "Please select a valid .apk file." });
      return;
    }
    setStatus({ type: "", message: "" });
    setSelectedFile(file);
  };

  const onDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    onFilePicked(event.dataTransfer.files?.[0]);
  };

  const uploadApkIfNeeded = async () => {
    if (!selectedFile) return uploadedApkUrl || currentConfig?.apkUrl || "";
    const formData = new FormData();
    formData.append("apk", selectedFile);

    setUploading(true);
    const response = await fetch(`${BASE_URL}/api/app-update/upload-apk`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });
    const data = await response.json();
    setUploading(false);

    if (!response.ok) {
      throw new Error(data.message || "APK upload failed");
    }

    const apkUrl = data?.data?.apkUrl || "";
    setUploadedApkUrl(apkUrl);
    return apkUrl;
  };

  const onSave = async () => {
    if (!latestVersion.trim()) {
      setStatus({ type: "error", message: "Latest version is required." });
      return;
    }
    if (role !== "admin") {
      setStatus({ type: "error", message: "Only admin can update this config." });
      return;
    }

    try {
      setSaving(true);
      setStatus({ type: "", message: "" });
      const apkUrl = await uploadApkIfNeeded();

      const response = await fetch(`${BASE_URL}/api/app-update/config`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          platform: "android",
          latestVersion: latestVersion.trim(),
          apkUrl,
          forceUpdate,
          releaseNotes: releaseNotes.trim(),
          isActive: true,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to save app update config");
      }

      setCurrentConfig(data.data);
      setSelectedFile(null);
      setStatus({ type: "success", message: "App update configuration saved." });
    } catch (error) {
      setStatus({ type: "error", message: error.message || "Save failed." });
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  const dropZoneStyle = useMemo(
    () => ({
      border: `2px dashed ${dragging ? "#2563eb" : "#c9d6ff"}`,
      borderRadius: 3,
      padding: 4,
      textAlign: "center",
      background: dragging ? "rgba(37, 99, 235, 0.08)" : "rgba(37, 99, 235, 0.02)",
      cursor: "pointer",
      transition: "all 0.2s ease",
    }),
    [dragging]
  );

  if (loading) {
    return (
      <Box sx={{ minHeight: "70vh", display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (role !== "admin") {
    return (
      <Container maxWidth="sm" sx={{ py: 10 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 4,
            border: "1px solid #f4c7c7",
            background: "linear-gradient(180deg, #fff7f7 0%, #ffffff 100%)",
          }}
        >
          <Stack spacing={2} alignItems="center">
            <Typography variant="h5" fontWeight={700}>
              Restricted Access
            </Typography>
            <Alert severity="error" sx={{ width: "100%" }}>
              Only admin can access this page.
            </Alert>
            <Button variant="contained" onClick={() => navigate(dashboardPath)} sx={{ borderRadius: 999 }}>
              Back to Dashboard
            </Button>
          </Stack>
        </Paper>
      </Container>
    );
  }

  return (
    <Container
      maxWidth={false}
      sx={{
        py: { xs: 2, md: 4 },
        px: { xs: 2, md: 4 },
        minHeight: "100vh",
        background:
          "linear-gradient(180deg, rgba(243,247,255,0.75) 0%, rgba(255,255,255,1) 55%, rgba(243,247,255,0.45) 100%)",
      }}
    >
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
        <Button
          variant="outlined"
          onClick={() => navigate(dashboardPath)}
          sx={{ borderRadius: 999, px: 2.5 }}
        >
          Back to Dashboard
        </Button>
      </Stack>
      <Stack direction={{ xs: "column", lg: "row" }} spacing={2.5}>
      <Paper
        elevation={0}
        sx={{
          flex: 2,
          p: { xs: 2.5, md: 4 },
          borderRadius: 4,
          border: "1px solid #e8ecff",
          background:
            "radial-gradient(circle at top right, rgba(37,99,235,0.12), rgba(37,99,235,0) 30%), #ffffff",
          boxShadow: "0 16px 40px rgba(17, 24, 39, 0.08)",
        }}
      >
        <Stack spacing={3.5}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={2}
          >
            <Box>
              <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: "-0.02em" }}>
                App Update Center
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Manage Android APK, versioning and release behavior in one place.
              </Typography>
            </Box>
            <Chip
              label={`Platform: ${currentConfig?.platform || "android"}`}
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
          </Stack>

          {status.message ? (
            <Alert severity={status.type || "info"} sx={{ borderRadius: 2 }}>
              {status.message}
            </Alert>
          ) : null}

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            sx={{
              p: 2,
              border: "1px solid #eef1ff",
              borderRadius: 3,
              background: "#fafbff",
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                CURRENT VERSION
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {currentConfig?.latestVersion || "Not set"}
              </Typography>
            </Box>
            <Box sx={{ flex: 2 }}>
              <Typography variant="caption" color="text.secondary">
                CURRENT APK URL
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, wordBreak: "break-all" }}>
                {currentConfig?.apkUrl || "No APK URL configured yet"}
              </Typography>
            </Box>
          </Stack>

          <Divider />

          <Box>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
              Upload APK
            </Typography>
            <Box
              onDragEnter={(event) => {
                event.preventDefault();
                setDragging(true);
              }}
              onDragOver={(event) => event.preventDefault()}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              onClick={() => document.getElementById("apk-file-input")?.click()}
              sx={dropZoneStyle}
            >
              <input
                id="apk-file-input"
                type="file"
                accept=".apk,application/vnd.android.package-archive"
                hidden
                onChange={(event) => onFilePicked(event.target.files?.[0])}
              />
              <Typography variant="subtitle1" fontWeight={700}>
                Drag and drop your APK here
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.8 }}>
                or click to select from your device
              </Typography>
              <Box sx={{ mt: 1.5 }}>
                <Chip
                  label={selectedFile ? `Selected: ${selectedFile.name}` : "No new APK selected"}
                  color={selectedFile ? "success" : "default"}
                  variant={selectedFile ? "filled" : "outlined"}
                />
              </Box>
            </Box>
          </Box>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              label="Latest Version"
              value={latestVersion}
              onChange={(event) => setLatestVersion(event.target.value)}
              placeholder="e.g. 3.0.5"
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch checked={forceUpdate} onChange={(event) => setForceUpdate(event.target.checked)} />
              }
              label={forceUpdate ? "Force update ON" : "Force update OFF"}
              sx={{
                m: 0,
                px: 2,
                border: "1px solid #e3e8ff",
                borderRadius: 2,
                minHeight: 56,
                justifyContent: "space-between",
                width: { xs: "100%", md: 260 },
              }}
            />
          </Stack>

          <TextField
            label="Release Notes"
            value={releaseNotes}
            onChange={(event) => setReleaseNotes(event.target.value)}
            fullWidth
            multiline
            minRows={4}
            placeholder="Describe fixes, improvements, and update notes..."
          />

          {(saving || uploading) && (
            <Box>
              <LinearProgress sx={{ borderRadius: 999 }} />
              <Typography variant="caption" color="text.secondary">
                {uploading ? "Uploading APK..." : "Saving update configuration..."}
              </Typography>
            </Box>
          )}

          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={fetchConfig}
              disabled={saving || uploading}
              sx={{ borderRadius: 999, px: 3 }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              onClick={onSave}
              disabled={saving || uploading}
              sx={{
                borderRadius: 999,
                px: 3,
                background: "linear-gradient(90deg, #1d4ed8 0%, #2563eb 100%)",
              }}
            >
              {saving || uploading ? "Saving..." : "Save Update Config"}
            </Button>
          </Stack>
        </Stack>
      </Paper>
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          p: { xs: 2.5, md: 3.5 },
          borderRadius: 4,
          border: "1px solid #e8ecff",
          background:
            "radial-gradient(circle at bottom left, rgba(37,99,235,0.12), rgba(37,99,235,0) 38%), #ffffff",
          boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)",
        }}
      >
        <Stack spacing={2}>
          <Typography variant="h6" fontWeight={800}>
            Update Workflow Guide
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Use this page to publish Android app updates that are consumed by the startup version
            check API.
          </Typography>
          <Divider />
          <Typography variant="subtitle2" fontWeight={700}>
            What happens when you save
          </Typography>
          <Chip label="1) APK is uploaded and URL is generated" color="primary" variant="outlined" />
          <Chip label="2) Version config is updated in database" color="primary" variant="outlined" />
          <Chip label="3) App startup API returns new update policy" color="primary" variant="outlined" />
          <Divider />
          <Typography variant="subtitle2" fontWeight={700}>
            Friendly reminders
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Double-check the version number and APK before saving so users get the right update
            experience.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            You can always come back and update these details anytime.
          </Typography>
        </Stack>
      </Paper>
      </Stack>
    </Container>
  );
};

export default AppUpdateAdmin;
